import React from 'react'
import { Placeholder } from 'semantic-ui-react'
import classnames from 'classnames/bind'
import { lightenColor, getCssColorFromColorName } from '../../../utils/colors'
import { ColorNames } from '../../../constants/colorVars'
import { AutomationTarget, SlottableComponent } from '../../../types'
import Animate from '../animate'

import styles from './styles.scss'

const cx = classnames.bind(styles)

export interface CardContainerProps
  extends AutomationTarget,
    SlottableComponent<'attachments'> {
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
  padding?: 'none' | 'extra-small' | 'small' | 'medium' | 'large' | 'x-large'
  borderColor?: ColorNames
  borderStyle?: 'none' | 'line' | 'gradient' | 'unset'
  borderRadius?: 'none' | 'extra-small' | 'small' | 'medium' | 'large'
  borderRadiusCorners?: 'all' | 'top' | 'bottom'
  dropShadow?: 'none' | 'large' | 'medium' | 'small'
  backgroundColor?: ColorNames | 'unset'
  loading?: boolean
}

const CardContainer: React.FC<CardContainerProps> = ({
  children,
  className,
  style,
  elmId,
  padding = 'medium',
  borderColor = ColorNames.GrayLight,
  borderStyle = 'none',
  borderRadius = 'medium',
  borderRadiusCorners = 'all',
  dropShadow = 'none',
  backgroundColor = ColorNames.White,
  loading,
  slots,
}) => {
  // extract raw color values from color names
  const borderColorRaw = getCssColorFromColorName(borderColor)
  const backgroundColorRaw =
    backgroundColor === 'unset'
      ? 'unset'
      : getCssColorFromColorName(backgroundColor)

  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  const gradientColor: string = lightenColor(borderColorRaw, 50)
  const gradientStyle =
    borderStyle !== 'gradient'
      ? { backgroundColor: backgroundColorRaw }
      : {
          background: `linear-gradient(${backgroundColorRaw}, ${backgroundColorRaw}) padding-box, linear-gradient(to bottom, ${gradientColor}, ${backgroundColorRaw}) border-box`,
        }
  const lineStyle =
    borderStyle === 'line' ? { borderColor: borderColorRaw } : {}

  return (
    <div className={cx('attachment-container')}>
      {slots?.attachments?.()}
      <div
        data-elm-id={elmId ? `${elmId}_card` : 'card'}
        className={cx(
          'card',
          `padding-${padding}`,
          `border-style-${borderStyle}`,
          `border-radius-${borderRadius}`,
          `border-radius-corners-${borderRadiusCorners}`,
          `drop-shadow-${dropShadow}`,
          className
        )}
        style={{ ...gradientStyle, ...lineStyle, ...style }}
      >
        {loading && (
          <div className={cx('loading-wrapper')}>
            <Animate.FadeInOut isVisible={loading} className={cx('overlap')}>
              <Placeholder
                className={cx(
                  'placeholder',
                  `border-radius-${borderRadius}`,
                  `border-radius-corners-${borderRadiusCorners}`,
                  'overlap'
                )}
              >
                <Placeholder.Image style={{ width: '100%' }} />
              </Placeholder>
            </Animate.FadeInOut>
            <div style={{ opacity: 0 }} className={cx('overlap')}>
              {children}
            </div>
          </div>
        )}
        {!loading && (
          <Animate.FadeInOut isVisible={!loading} className={className}>
            {children}
          </Animate.FadeInOut>
        )}
      </div>
    </div>
  )
}

// Extending component with static property for colorNames for use externally
interface CardStatic {
  ColorNames: typeof ColorNames
}

const CardContainerWithStatic: React.FC<CardContainerProps> & CardStatic =
  CardContainer as React.FC<CardContainerProps> & CardStatic

CardContainerWithStatic.ColorNames = ColorNames

export default CardContainerWithStatic
